import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowLeft from './images/ArrowLeft.png';
import Search from './images/Search.png';
import Microphone from './images/Microphone.png';
import CartBag from './images/CartBag.png';
import VendorItemsSkeleton from './SkeletonLoader/VendorItemsSkeleton';
import BucketEmptyConfirmPopup from './Component/BucketEmptyConfirmPopup';
import ViewBucket from './Component/ViewBucket';

const Vendoritems = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { vendorId, vendorName, vendorImage } = location.state || {};
  
  const [searchQuery, setSearchQuery] = useState('');
  const [vendorProducts, setVendorProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState({});
  const [bucketImages, setBucketImages] = useState({});
  const [bucketProducts, setBucketProducts] = useState({});
  const [showEmptyBucketPopup, setShowEmptyBucketPopup] = useState(false);
  const [pendingRemoveProductId, setPendingRemoveProductId] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); 
  
  const bucketRef = useRef(null);
  const imgRefs = useRef({});
  const imageUrl = process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES;

  useEffect(() => {
    if (vendorId) {
      fetchVendorProducts();
    }
    loadBucketData();
  }, [vendorId]);


  const loadBucketData = () => {
  const savedBucketProducts = localStorage.getItem('bucketProducts');

  if (savedBucketProducts) {
    const products = JSON.parse(savedBucketProducts);
    setBucketProducts(products);

    // Build quantities from product data
    const quantities = {};
    const images = {};

    Object.values(products).forEach(product => {
      if (product._id) {
        quantities[product._id] = product.quantity || 0;
        if (product.product_image) {
          images[product._id] = true; // or store actual image path if needed
        }
      }
    });

    setQuantities(quantities);
    setBucketImages(images);
  } else {
    setQuantities({});
    setBucketImages({});
  }
};


  const fetchVendorProducts = async () => {
  try {
    setLoading(true);
    
    // Get token from userData in sessionStorage
    const userData = JSON.parse(sessionStorage.getItem('userData'));
    const token = userData?.token || sessionStorage.getItem('token');
    
    if (!token) {
      console.error('No authentication token found');
      setLoading(false);
      return;
    }
    
    const response = await fetch(
      `${process.env.REACT_APP_FORE_BRICKBUCKET}product/get-quick-buy-products-by-vendor?vendorId=${vendorId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token,
        },
      }
    );

    const data = await response.json();
    console.log('Vendor products response:', data);
    
    if (data.success) {
      setVendorProducts(data.data || []);
    } else {
      console.error('Failed to fetch vendor products:', data.message);
      // Handle specific error cases
      if (data.message.includes('No product categories found')) {
        console.log('This vendor has no product categories assigned');
      } else if (data.message.includes('No e-commerce product categories')) {
        console.log('This vendor has no e-commerce enabled categories');
      }
    }
  } catch (error) {
    console.error('Error fetching vendor products:', error);
  } finally {
    setLoading(false);
  }
};

const handleQuantityChange = (productId, action) => {
  // Always parse localStorage safely
  const currentBucketProducts = JSON.parse(localStorage.getItem('bucketProducts') || '{}');

  if (action === 'increase') {
    setQuantities(prev => {
      const newQty = (prev[productId] || 0) + 1;
      const newQuantities = { ...prev, [productId]: newQty };

      // Dispatch custom event to notify MenuFooter
      window.dispatchEvent(new CustomEvent('bucketUpdated'));

      // Find product
      const product = vendorProducts.find(p => p._id === productId);
      if (product) {
        currentBucketProducts[productId] = {
          ...product,
          quantity: newQty,
        };

        localStorage.setItem('bucketProducts', JSON.stringify(currentBucketProducts));
        setBucketProducts({ ...currentBucketProducts });
      }

      return newQuantities;
    });

    // Flying animation → only if first time adding
    if (!bucketImages[productId]) {
      setTimeout(() => {
        const imgEl = imgRefs.current?.[productId];
        const bucketEl = bucketRef.current;
        if (imgEl && bucketEl) {
          const fromRect = imgEl.getBoundingClientRect();
          const toRect = bucketEl.getBoundingClientRect();

          const flyingImg = document.createElement('img');
          flyingImg.src = `${imageUrl}${vendorProducts.find(p => p._id === productId)?.product_image || ''}`;
          flyingImg.className =
            'fixed z-50 w-12 h-12 object-contain bg-white rounded-full shadow-xl border-2 border-red-400 pointer-events-none';
          flyingImg.style.top = `${fromRect.top + fromRect.height / 2 - 24}px`;
          flyingImg.style.left = `${fromRect.left + fromRect.width / 2 - 24}px`;
          flyingImg.style.transition = 'all 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
          flyingImg.style.zIndex = '9999';

          document.body.appendChild(flyingImg);

          // Animate movement
          setTimeout(() => {
            flyingImg.style.top = `${toRect.top + toRect.height / 2 - 12}px`;
            flyingImg.style.left = `${toRect.left + toRect.width / 2 - 12}px`;
            flyingImg.style.transform = 'scale(0.5)';
            flyingImg.style.opacity = '0.3';
          }, 100);

          // Cleanup & update bucketImages
          setTimeout(() => {
            setBucketImages(prev => {
              const newBucketImages = { ...prev, [productId]: true };
             // localStorage.setItem('bucketImages', JSON.stringify(newBucketImages));
              return newBucketImages;
            });

            if (document.body.contains(flyingImg)) {
              document.body.removeChild(flyingImg);
            }
          }, 1000);
        }
      }, 100);
    }
  } 
  
  else if (action === 'decrease') {
    const currentQty = quantities[productId] || 0;
    const totalItemsInBucket = Object.values(quantities).reduce((total, qty) => total + qty, 0);

    if (currentQty === 1 && totalItemsInBucket === 1) {
      // Last item in bucket → confirmation popup
      setPendingRemoveProductId(productId);
      setShowEmptyBucketPopup(true);
      return;
    }else{
    performDecrease(productId);

    }
    
  }
};

const handleMainProductQuantityInputChange = (productid, value) => {
  // Parse value as number (handles string from input) and default to 1 if falsy, <=0, or <1
  const parsedValue = parseInt(value, 10) || 0; // Parse to int, default to 0 if invalid/empty
  const effectiveValue = parsedValue < 1 ? 1 : parsedValue; // If <1 (including 0, negative, or invalid), set to 1

  // Parse current bucket from localStorage
  const currentBucketProducts = JSON.parse(localStorage.getItem('bucketProducts') || '{}');
  const mainProductId = productid

  // Find the main product (adjust based on your data source, e.g., relatedProducts or a separate mainProduct)
  const productData = currentBucketProducts[mainProductId]; // Use mainProduct if separate

  if (productData) {
    // Always update/add the product in the bucket with the effective quantity (no deletion, just change quantity)
    currentBucketProducts[mainProductId] = {
      ...productData,
      quantity: effectiveValue,
    };
    const quantitiesData = quantities
          quantitiesData[mainProductId] = effectiveValue

          setQuantities(quantitiesData)

    // Persist to localStorage and update state
    localStorage.setItem('bucketProducts', JSON.stringify(currentBucketProducts));
    setBucketProducts({ ...currentBucketProducts });

    // Dispatch the event after the bucket has been updated
    window.dispatchEvent(new CustomEvent('bucketUpdated'));
  }
};


const performDecrease = (productId) => {
  const currentBucketProducts = JSON.parse(localStorage.getItem('bucketProducts') || '{}');

  setQuantities(prev => {
    const newQty = Math.max(0, (prev[productId] || 1) - 1);
    const newQuantities = { ...prev, [productId]: newQty };

    // Update or remove product
    if (currentBucketProducts[productId]) {
      if (newQty > 0) {
        currentBucketProducts[productId] = {
          ...currentBucketProducts[productId],
          quantity: newQty,
        };
      } else {
        delete currentBucketProducts[productId];
      }
      localStorage.setItem('bucketProducts', JSON.stringify(currentBucketProducts));
      setBucketProducts({ ...currentBucketProducts });
    }

    // Notify MenuFooter
    window.dispatchEvent(new CustomEvent('bucketUpdated'));

    return newQuantities;
  });
};

const handleConfirm = async() => {
 await performDecrease (pendingRemoveProductId)
  setShowEmptyBucketPopup(false);
  setPendingRemoveProductId(null);
  setPendingAction(null);
};

  const handleViewBucket = () => {
    navigate('/consumer/cartpreview');
  };

  const filteredProducts = vendorProducts.filter(product =>
    product.product_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );


 if (loading) {
    return <VendorItemsSkeleton />;
  }



  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className='border-b border-gray-300 mb-4'>
        <div className="px-1 bg-white py-3 flex justify-between items-center sticky top-0 z-30">
          <div className="flex items-center">
            <button onClick={() => navigate(-1)} className="text-gray-700 p-1 hover:bg-gray-100 rounded-md mr-1">
              <img 
                src={ArrowLeft} 
                alt="Back" 
                className="w-10 h-6" 
              />
            </button>
            <div className="flex items-center">
              {vendorImage && (
                <img
                  src={`${imageUrl}${vendorImage}`}
                  alt={vendorName}
                  className="w-6 h-6 object-contain mr-1"
                />
              )}
              <h2 className="text-md font-medium text-[#000000]">
                {vendorName || 'Vendor Products'}
              </h2>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      {/* <div className="px-4 pt-6 pb-2 bg-white sticky top-0 z-20">
        <div className="relative max-w-xl mx-auto">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 text-lg">
            <img
              src={Search}
              alt="Search"
              className="h-6 w-6"
            />
          </span>
          <input
            type="text"
            placeholder='Search "product name"'
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-12 py-2 rounded-md border border-gray-300 text-sm font-medium focus:outline-none"
          />
          
          <button
            className="absolute inset-y-0 right-0 flex items-center pr-3"
            onClick={() => searchQuery && setSearchQuery('')}
          >
            {searchQuery ? (
              <div className="w-5 h-5 bg-gray-400 hover:bg-gray-500 rounded-full flex items-center justify-center transition-colors">
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            ) : (
              <img
                src={Microphone}
                alt="Voice Search"
                className="w-6 h-8"
              />
            )}
          </button>
        </div>
      </div> */}

      {/* Products Grid */}
      <div className="px-4 py-4 pb-10 bg-white">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="text-gray-500">Loading products...</div>
          </div>
        ) : filteredProducts.length > 0 ? (
          <div className="bg-white">
            <div className="grid grid-cols-2 gap-4">
              {filteredProducts.map(product => {
                const quantity = quantities[product._id] || 0;
                return (
                  <div key={product._id} className="flex flex-col">
                    {/* Main Container for Image and Add Button with Gray Background */}
                    <div className="rounded-xl bg-[#F5F5F5] overflow-hidden mb-3 relative pb-12 mx-1">
                      {/* Image Section */}
                      <div className="h-24 flex items-center justify-center p-3">
                        <img
                          ref={el => (imgRefs.current[product._id] = el)}
                          src={`${imageUrl}${product.product_image}`}
                          alt={product.product_name}
                          className="h-full w-full object-contain cursor-pointer hover:scale-105 transition-transform duration-200"
                          onClick={() => navigate('/consumer/productInformation', {
                            state: {
                              product: product,
                              relatedProducts: filteredProducts,
                              vendorId: vendorId
                            }
                          })}
                        />
                      </div>
                                                       
                      {/* Add Button - Inside the same gray container */}
                      <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2">
                        {quantity === 0 ? (
                          <button
                            onClick={() => handleQuantityChange(product._id, 'increase')}
                            className="text-xs text-[#D93025] bg-white border border-red-500 rounded-md px-6 py-1 font-semibold hover:bg-red-50 transition shadow-sm"
                          >
                            Add
                          </button>
                        ) : (
                          <div className="flex items-center justify-between bg-white border border-red-500 rounded-md px-3 py-1 shadow-sm">
                            <button
                              onClick={() => handleQuantityChange(product._id, 'decrease')}
                              className="w-5 h-5 flex items-center justify-center text-red-500 text-sm font-bold"
                            >
                              −
                            </button>
                            <input
                        type="number"
                        value={quantity}
                        onChange={(e) => handleMainProductQuantityInputChange(product._id, e.target.value)} // New handler for direct input changes
                        onFocus={(e) => {
                          e.target.select(); // Select all text on focus for easy editing
                        }}
                        onBlur={(e) => {
                          // Optional: Ensure value is at least 1 on blur (if needed, but handler already enforces this)
                        }}
                        min="1"
                        className="w-10 text-center text-sm text-gray-900 font-medium border-0 bg-transparent focus:outline-none focus:ring-0 focus:border-transparent mx-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="1"
                      />
                            <button
                              onClick={() => handleQuantityChange(product._id, 'increase')}
                              className="w-5 h-5 flex items-center justify-center text-red-500 text-sm font-bold"
                            >
                              +
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Product Info - Outside the gray container */}
                    <div className="flex flex-col items-start mx-1">
                      <h3 className="text-xs font-medium text-[#000000] mb-1 line-clamp-2 leading-snug text-left">
                        {product.product_name}
                      </h3>
                      <p className="text-xs text-[#170101] font-semibold">
                        ₹{product.selling_price?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-start mt-6 px-4">
            <img
              src={CartBag}
              alt="No products found"
              className="w-24 h-24 object-contain mb-1 opacity-60"
            />
            <h3 className="text-center text-xl font-semibold text-gray-600 mb-1">Sorry!</h3>
                       <div className="text-center text-gray-500 text-sm">
              {searchQuery ?
                `Currently we don't have "${searchQuery}" in ${vendorName || 'this vendor'}'s products` :
                `No products available from ${vendorName || 'this vendor'}`
              }
            </div>
          </div>
        )}
      </div>

      {/* View Bucket Button - Fixed at bottom */}

           <ViewBucket
              bucketProducts={bucketProducts}
              imageUrl={imageUrl}
              bucketRef={bucketRef}
              onViewBucket={handleViewBucket}
            />

    <BucketEmptyConfirmPopup
      isOpen={showEmptyBucketPopup}
      onClose={() => {
        setShowEmptyBucketPopup(false);
        setPendingRemoveProductId(null);
        setPendingAction(null);
      }}
      onConfirm={handleConfirm}
      productName={
        pendingRemoveProductId 
          ? (pendingAction === 'main' 
              ?  vendorProducts.find(p => p._id === pendingRemoveProductId)?.product_name : ''
            ) || "this item"
          : "this item"
      }
    />

    </div>
  );
};

export default Vendoritems;
